import { auth } from '../../firebaseConfig';
import { 
  Workflow, 
  WorkflowDocument,
  Payment
} from '../../types/workflowTypes';
import { normalizeTimestamp } from './utils/dataUtils';
import { getEffectiveScopeUid } from '../../utils/tenantScope';

// Typendefinitionen für API-Antworten
interface SuccessResponse<T> {
  success: boolean;
  data: T;
}

/**
 * Service für die Interaktion mit der vereinfachten Workflow-API
 * Diese Implementierung verwendet die Backend-API anstelle von direkten Firestore-Abfragen
 */
export class SimpleWorkflowAPIService {
  /**
   * Lädt alle Workflows für den aktuellen Benutzer
   * @param userId - Optionale Benutzer-ID, sonst wird der authentifizierte Benutzer verwendet
   * @param params - Optionale API-Parameter
   * @returns Array von Workflow-Objekten
   */
  static async getWorkflows(userId?: string, params?: Record<string, any>): Promise<Workflow[]> {
    try {
      const currentUserId = userId || getEffectiveScopeUid() || localStorage.getItem('userId');
      
      if (!currentUserId) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      const { getWorkflows: fetchWorkflowsFromApi } = await import('../../api/workflowApi');
      
      // API-Parameter vorbereiten
      const apiParams = params || {
        orderBy: 'updatedAt',
        orderDirection: 'desc'
      };
      
      // TypeScript-Cast, um zu erzwingen, dass der Compiler versteht, dass die Funktion 2 Parameter akzeptiert
      // Anstatt fetchFunction definieren wir einen aussagekräftigeren Aufruf
      const response = await fetchWorkflowsFromApi(currentUserId, apiParams);
      
      // --- Logging der Rohdaten vom Backend ---
      // Entfernt: console.log für Rohdaten
      // --- Ende Logging ---

      let workflowsData: any[] = [];
      
      if (Array.isArray(response)) {
        workflowsData = response;
      } else if (response && 'success' in response && 'data' in response && Array.isArray(response.data)) {
        workflowsData = response.data;
      } else {
        console.error('Unerwartete API-Antwortstruktur für Workflows:', response);
        return [];
      }

      return workflowsData.map(rawWorkflow => {
        const createdAt = normalizeTimestamp(rawWorkflow.createdAt);
        const updatedAt = normalizeTimestamp(rawWorkflow.updatedAt);
        const metadata = rawWorkflow.metadata && typeof rawWorkflow.metadata === 'object' && !Array.isArray(rawWorkflow.metadata) 
                         ? rawWorkflow.metadata 
                         : {}; // Sicherstellen, dass metadata ein Objekt ist

        // 1. Kundeninformationen konsolidieren
        let customer: any = {}; // Typ any hier, um flexibel zu bleiben
        if (rawWorkflow.customer && typeof rawWorkflow.customer === 'object') {
          customer = rawWorkflow.customer;
        } else if (rawWorkflow.customerInfo && typeof rawWorkflow.customerInfo === 'object') {
          customer = rawWorkflow.customerInfo;
        } else if (metadata.customer && typeof metadata.customer === 'object') {
          customer = metadata.customer;
        } else if (metadata.customerName || metadata.customerId) {
          customer = {
            id: metadata.customerId || crypto.randomUUID(),
            companyName: metadata.customerName || 'Unbekannter Kunde',
            personName: metadata.customerName, // Fallback, wenn nur ein Name vorhanden
          };
        }
        // Fallback-Namen, falls immer noch leer
        if (!customer.companyName && !customer.personName) {
            customer.companyName = 'Unbekannter Kunde';
        }

        // 2. Dokumente konsolidieren und mappen
        const documentsRaw = Array.isArray(rawWorkflow.documents) ? rawWorkflow.documents : [];
        const documents: WorkflowDocument[] = documentsRaw.map((doc: any) => {
          // ---- DEBUG LOGGING HINZUGEFÜGT ----
          // Entfernt: console.log für Roh-Dokument
          // -------------------------------------
          
          // Priorisiere documentType für den Typ, falle zurück auf type
          // Das ist wichtig, da workflow_stages documentType hat, während invoice-Objekte type haben.
          const docType = doc.documentType || doc.type || 'unknown';
          
          // TotalAmount: Direkt aus doc.totalAmount oder doc.amount nehmen.
          // Wenn dies für XRechnungen (aus workflow_stages kommend) fehlt, ist das ein Backend-Datenproblem,
          // da der Frontend-Service hier nicht ohne weiteres den zugehörigen Invoice-Eintrag laden kann.
          const docTotalAmount = parseFloat(String(doc.amount || doc.totalAmount || '0').replace(/,/g, '.'));
          const docCurrency = doc.currency || 'EUR';

          const mappedDoc = {
            id: doc.id || doc.documentId || crypto.randomUUID(),
            type: docType,
            number: doc.number || doc.documentNumber || doc.invoiceNumber || 'N/A', // invoiceNumber aus workflow_stages
            date: doc.date || doc.documentDate || new Date().toISOString(),
            status: doc.status || 'unknown',
            totalAmount: docTotalAmount,
            currency: docCurrency
          };
          // ---- DEBUG LOGGING HINZUGEFÜGT ----
          // Entfernt: console.log für gemapptes Dokument
          // -------------------------------------
          return mappedDoc;
        });
        
        const sortedDocuments = [...documents].sort((a, b) => 
          (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0)
        );
        const latestDocument = sortedDocuments.length > 0 ? sortedDocuments[0] : undefined;

        // 3. Workflow-Name bestimmen
        let workflowName = rawWorkflow.name || rawWorkflow.displayName;
        if (!workflowName && (customer.companyName && customer.companyName !== 'Unbekannter Kunde')) {
          workflowName = `Vorgang: ${customer.companyName}`;
        } else if (!workflowName && customer.personName) {
          workflowName = `Vorgang: ${customer.personName}`;
        } else if (!workflowName && latestDocument && latestDocument.number !== 'N/A') {
          let docTypeLabel = 'Dokument';
          switch (latestDocument.type) {
            case 'invoice': docTypeLabel = 'Rechnung'; break;
            case 'offer': docTypeLabel = 'Angebot'; break;
            case 'xrechnung': docTypeLabel = 'XRechnung'; break;
            case 'down_payment': docTypeLabel = 'Abschlagsrechnung'; break;
            case 'final_invoice': docTypeLabel = 'Abschlussrechnung'; break;
            default: docTypeLabel = 'Dokument'; break;
          }
          workflowName = `${docTypeLabel} ${latestDocument.number}`;
        } else if (!workflowName) {
          workflowName = 'Unbenannter Vorgang';
        }

        // 4. Gesamtwert und Währung bestimmen
        let totalAmount = 0;
        let currency = 'EUR';

        if (metadata.totalAmount) {
          totalAmount = parseFloat(String(metadata.totalAmount).replace(/,/g, '.'));
        } else if (latestDocument?.totalAmount) {
          totalAmount = latestDocument.totalAmount;
        } else if (documents.length > 0) {
            // Summiere alle Dokumente, falls kein spezifischer Gesamtbetrag vorhanden ist
            totalAmount = documents.reduce((sum, doc) => sum + doc.totalAmount, 0);
        }
        
        if (metadata.currency) {
          currency = metadata.currency;
        } else if (latestDocument?.currency) {
          currency = latestDocument.currency;
        } // Standard ist EUR
        
        // Konsistentes Metadata-Objekt für die Rückgabe
        const finalMetadata = {
            ...metadata, // Behalte bestehende Metadaten
            totalAmount: totalAmount, // Überschreibe/Setze konsolidierten Wert
            currency: currency,       // Überschreibe/Setze konsolidierte Währung
            customerName: customer.companyName || customer.personName,
            customerId: customer.id
        };

        return {
          id: rawWorkflow.id || crypto.randomUUID(),
          createdAt,
          updatedAt,
          status: rawWorkflow.status || 'draft',
          documents: sortedDocuments,
          latestDocument,
          createdBy: rawWorkflow.createdBy || rawWorkflow.userId || auth.currentUser?.uid,
          customer,
          payments: rawWorkflow.payments || [],
          tags: rawWorkflow.tags || [],
          name: workflowName,
          description: rawWorkflow.description || '',
          processType: rawWorkflow.processType || 'standard',
          referenceId: rawWorkflow.referenceId || rawWorkflow.id || crypto.randomUUID(),
          metadata: finalMetadata, 
        } as Workflow;
      });
    } catch (error) {
      console.error('Fehler in getWorkflows:', error);
      return [];
    }
  }

  /**
   * Lädt einen einzelnen Workflow anhand seiner ID
   * @param workflowId - Workflow-ID
   * @returns Workflow-Objekt
   */
  static async getWorkflowById(workflowId: string): Promise<Workflow | null> {
    try {
      if (!auth.currentUser) {
        throw new Error('Benutzer nicht authentifiziert');
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SimpleWorkflowAPIService] Lade Workflow ${workflowId} über Backend-API`);
      }
      
      const { getWorkflow } = await import('../../api/workflowApi');
      const response = await getWorkflow(workflowId);

      let workflowData: any;
      if (response && 'success' in response && 'data' in response) {
        workflowData = response.data;
      } else if (response && 'id' in response) {
        workflowData = response;
      } else {
        console.error('API-Fehler beim Abrufen des Workflows:', response);
        return null;
      }

      // ---- Beginn der vereinheitlichten Transformation -----
      const createdAt = normalizeTimestamp(workflowData.createdAt);
      const updatedAt = normalizeTimestamp(workflowData.updatedAt);
      const metadata = workflowData.metadata && typeof workflowData.metadata === 'object' && !Array.isArray(workflowData.metadata) 
                       ? workflowData.metadata 
                       : {};

      let customer: any = {};
      if (workflowData.customer && typeof workflowData.customer === 'object') {
        customer = workflowData.customer;
      } else if (workflowData.customerInfo && typeof workflowData.customerInfo === 'object') {
        customer = workflowData.customerInfo;
      } else if (metadata.customer && typeof metadata.customer === 'object') {
        customer = metadata.customer;
      } else if (metadata.customerName || metadata.customerId) {
        customer = {
          id: metadata.customerId || crypto.randomUUID(),
          companyName: metadata.customerName || 'Unbekannter Kunde',
          personName: metadata.customerName,
        };
      }
      if (!customer.companyName && !customer.personName) {
          customer.companyName = 'Unbekannter Kunde';
      }

      const documentsRaw = Array.isArray(workflowData.documents) ? workflowData.documents : [];
      const documents: WorkflowDocument[] = documentsRaw.map((doc: any) => ({
        id: doc.id || doc.documentId || crypto.randomUUID(),
        type: doc.type || 'unknown',
        number: doc.number || doc.documentNumber || 'N/A',
        date: doc.date || doc.documentDate || new Date().toISOString(),
        status: doc.status || 'unknown',
        totalAmount: parseFloat(String(doc.amount || doc.totalAmount || '0').replace(/,/g, '.')),
        currency: doc.currency || 'EUR'
      }));
      
      const sortedDocuments = [...documents].sort((a, b) => 
        (b.date ? new Date(b.date).getTime() : 0) - (a.date ? new Date(a.date).getTime() : 0)
      );
      const latestDocument = sortedDocuments.length > 0 ? sortedDocuments[0] : undefined;

      let workflowName = workflowData.name || workflowData.displayName;
      if (!workflowName && (customer.companyName && customer.companyName !== 'Unbekannter Kunde')) {
        workflowName = `Vorgang: ${customer.companyName}`;
      } else if (!workflowName && customer.personName) {
        workflowName = `Vorgang: ${customer.personName}`;
      } else if (!workflowName && latestDocument && latestDocument.number !== 'N/A') {
        let docTypeLabel = 'Dokument';
        switch (latestDocument.type) {
          case 'invoice': docTypeLabel = 'Rechnung'; break;
          case 'offer': docTypeLabel = 'Angebot'; break;
          case 'xrechnung': docTypeLabel = 'XRechnung'; break;
          case 'down_payment': docTypeLabel = 'Abschlagsrechnung'; break;
          case 'final_invoice': docTypeLabel = 'Abschlussrechnung'; break;
          default: docTypeLabel = 'Dokument'; break;
        }
        workflowName = `${docTypeLabel} ${latestDocument.number}`;
      } else if (!workflowName) {
        workflowName = 'Unbenannter Vorgang';
      }

      let totalAmount = 0;
      let currency = 'EUR';
      if (metadata.totalAmount) {
        totalAmount = parseFloat(String(metadata.totalAmount).replace(/,/g, '.'));
      } else if (latestDocument?.totalAmount) {
        totalAmount = latestDocument.totalAmount;
      } else if (documents.length > 0) {
          totalAmount = documents.reduce((sum, doc) => sum + doc.totalAmount, 0);
      }
      if (metadata.currency) {
        currency = metadata.currency;
      } else if (latestDocument?.currency) {
        currency = latestDocument.currency;
      }
      
      const finalMetadata = {
          ...metadata,
          totalAmount: totalAmount,
          currency: currency,
          customerName: customer.companyName || customer.personName,
          customerId: customer.id
      };
      // ---- Ende der vereinheitlichten Transformation -----

      return {
        id: workflowData.id || crypto.randomUUID(),
        createdAt,
        updatedAt,
        status: workflowData.status || 'draft',
        documents: sortedDocuments,
        latestDocument,
        createdBy: workflowData.createdBy || workflowData.userId || auth.currentUser?.uid,
        customer,
        payments: workflowData.payments || [],
        tags: workflowData.tags || [],
        name: workflowName,
        description: workflowData.description || '',
        processType: workflowData.processType || 'standard',
        referenceId: workflowData.referenceId || workflowData.id || crypto.randomUUID(),
        metadata: finalMetadata,
        events: workflowData.events || [],
      } as Workflow;
    } catch (error) {
      console.error('Fehler in getWorkflowById:', error);
      return null;
    }
  }

  /**
   * Erstellt einen neuen Workflow mit einem anfänglichen Dokument
   * @param initialDocument Erstes Dokument
   * @param customer Kundendaten
   * @param userId Benutzer-ID
   * @returns Erstelltes Workflow-Objekt
   */
  static async createWorkflow(
    initialDocument: WorkflowDocument,
    customer: any,
    userId: string
  ): Promise<Workflow | null> {
    try {
      if (!auth.currentUser) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      const { createWorkflow, addDocumentToWorkflow } = await import('../../api/workflowApi');
      
      const customerInfoForApi = {
        id: customer.id || customer.customerId || crypto.randomUUID(),
        name: customer.companyName || customer.name || customer.personName || 'Unbekannter Kunde',
        email: customer.email || '',
        phone: customer.phone || '',
        street: customer.street || '',
        zip: customer.zip || '',
        city: customer.city || '',
        country: customer.country || 'DE',
      };
      
      const initialStatus = 'draft';
      // Metadaten für den createWorkflow API Call können minimal sein, 
      // da getWorkflowById die Details später anreichert.
      const metadataForApi = {
        customerName: customerInfoForApi.name,
        customerId: customerInfoForApi.id,
        // Fügen Sie hier ggf. weitere initiale Metadaten hinzu, die direkt beim Erstellen wichtig sind
      };
      
      const createResponse = await createWorkflow(customerInfoForApi, initialStatus, metadataForApi);
      
      let newWorkflowData: any;
      if (createResponse && 'success' in createResponse && 'data' in createResponse) {
        newWorkflowData = createResponse.data;
      } else if (createResponse && 'id' in createResponse) {
        newWorkflowData = createResponse;
      } else {
        console.error('API-Fehler beim Erstellen des Workflows:', createResponse);
        return null;
      }

      // Stelle sicher, dass das initiale Dokument alle notwendigen Felder hat
      const documentToSend: any = {
        id: initialDocument.id || crypto.randomUUID(),
        type: initialDocument.type as 'invoice' | 'offer' || 'unknown',
        number: initialDocument.number || 'N/A',
        date: initialDocument.date ? new Date(initialDocument.date).toISOString() : new Date().toISOString(),
        status: initialDocument.status || 'draft',
        amount: initialDocument.totalAmount || 0,
        currency: initialDocument.currency || 'EUR'
      };
      
      // Dokument-ID muss für den API-Aufruf documentId heißen, falls die API das erwartet
      // Passe dies an die Erwartungen deiner `addDocumentToWorkflow` API an.
      // Hier gehen wir davon aus, dass die API `id` für das Dokument erwartet.
      await addDocumentToWorkflow(newWorkflowData.id, documentToSend);

      // Hole den frisch erstellten und angereicherten Workflow über getWorkflowById
      return await SimpleWorkflowAPIService.getWorkflowById(newWorkflowData.id);
    } catch (error) {
      console.error('Fehler in createWorkflow:', error);
      return null;
    }
  }

  /**
   * Fügt ein Dokument zu einem Workflow hinzu
   * @param workflowId ID des Workflows
   * @param documentData Dokumentdaten
   * @returns Aktualisiertes Workflow-Objekt
   */
  static async addDocumentToWorkflow(
    workflowId: string,
    documentData: Partial<WorkflowDocument> // Erlaube partielle Updates/Ergänzungen
  ): Promise<Workflow | null> {
    try {
      if (!auth.currentUser) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      const { addDocumentToWorkflow: apiAddDocument } = await import('../../api/workflowApi');
      
      // Aufbereiten der Dokumentdaten für die API
      const docToSend = {
        id: documentData.id || crypto.randomUUID(),
        type: documentData.type || 'unknown',
        number: documentData.number || 'N/A',
        date: documentData.date ? new Date(documentData.date).toISOString() : new Date().toISOString(),
        status: documentData.status || 'draft',
        amount: documentData.totalAmount || 0,
        currency: documentData.currency || 'EUR',
        // Weitere Felder könnten hier basierend auf der API-Definition nötig sein
      };
      
      await apiAddDocument(workflowId, docToSend as any); // Cast zu any, falls API spezifische Struktur erwartet
      
      return await SimpleWorkflowAPIService.getWorkflowById(workflowId);
    } catch (error) {
      console.error('Fehler in addDocumentToWorkflow:', error);
      return null;
    }
  }

  /**
   * Aktualisiert den Status eines Workflows
   * @param workflowId ID des Workflows
   * @param newStatus Neuer Status
   * @param commentOrDetails Optionaler Kommentar oder Detailobjekt mit Dokumentinformationen
   * @returns Aktualisiertes Workflow-Objekt
   */
  static async updateWorkflowStatus(
    workflowId: string,
    newStatus: string,
    commentOrDetails?: string | Record<string, any>
  ): Promise<Workflow | null> {
    try {
      if (!auth.currentUser) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      const { updateWorkflowStatus: apiUpdateStatus } = await import('../../api/workflowApi');
      
      let details: Record<string, any> = {};
      
      // Unterscheide zwischen String (Kommentar) und Objekt (Details)
      if (typeof commentOrDetails === 'string') {
        details = { comment: commentOrDetails }; // Bisheriges Verhalten für String-Parameter
      } else if (commentOrDetails && typeof commentOrDetails === 'object') {
        details = commentOrDetails; // Verwende direkt das Objekt mit Dokumentdetails
      }
      
      if (process.env.NODE_ENV === 'development') {
        console.log(`[SimpleWorkflowAPIService] Status-Update für Workflow ${workflowId}`, {
          status: newStatus,
          details
        });
      }
      
      await apiUpdateStatus(
        workflowId, 
        newStatus, 
        details
      );
      
      return await SimpleWorkflowAPIService.getWorkflowById(workflowId);
    } catch (error) {
      console.error('Fehler in updateWorkflowStatus:', error);
      return null;
    }
  }

  /**
   * Registriert eine Zahlung für einen Workflow
   * @param workflowId ID des Workflows
   * @param payment Zahlungsdaten
   * @returns Aktualisiertes Workflow-Objekt
   */
  static async registerPayment(
    workflowId: string,
    payment: Payment // Payment Typ aus workflowTypes.ts
  ): Promise<Workflow | null> {
    try {
      if (!auth.currentUser) {
        throw new Error('Benutzer nicht authentifiziert');
      }

      const { registerPayment: apiRegisterPayment } = await import('../../api/workflowApi');
      
      const newStatus = payment.paymentType === 'full' ? 'paid' : 'partially_paid';
      
      let paymentDateString: string;
      if (!payment.paymentDate) {
        paymentDateString = new Date().toISOString().split('T')[0];
      } else if (typeof payment.paymentDate === 'string') {
        paymentDateString = payment.paymentDate;
      } else if (payment.paymentDate instanceof Date) {
        paymentDateString = payment.paymentDate.toISOString().split('T')[0];
      } else {
        paymentDateString = new Date().toISOString().split('T')[0]; // Fallback
      }
      
      const paymentDetailsForApi = {
        documentId: payment.documentId,
        documentNumber: payment.documentNumber,
        amount: payment.amount,
        currency: payment.currency || 'EUR',
        paymentDate: paymentDateString,
        paymentType: payment.paymentType,
        paymentMethod: payment.paymentMethod || 'manual',
        note: payment.note
      };
      
      const comment = payment.note || `Zahlung ${payment.paymentType === 'full' ? 'vollständig' : 'teilweise'} registriert: ${payment.amount} ${payment.currency || 'EUR'}`;
      
      // Verwende das alte Parameter-Format für die Kompatibilität
      await apiRegisterPayment(
        workflowId, 
        newStatus, 
        paymentDetailsForApi, 
        comment
      );
      
      return await SimpleWorkflowAPIService.getWorkflowById(workflowId);
    } catch (error) {
      console.error('Fehler in registerPayment:', error);
      return null;
    }
  }
} 